import { CaretRightOutlined } from "@ant-design/icons";
import { ReactComponent as NavToFirst } from "../../assets/Icons/navToFirst.svg";
import { ReactComponent as NavToLast } from "../../assets/Icons/navToLast.svg";
import { ReactComponent as PrevIcon } from "../../assets/Icons/prevIcon.svg";
import { ReactComponent as NextIcon } from "../../assets/Icons/nextIcon.svg";
import { Space, Table, Pagination, Select, Button, ConfigProvider } from "antd";
import React, { useState, useEffect, useRef } from "react";
import { ReactComponent as DownArrow } from "../../assets/Icons/DownArrow.svg";
import { ReactComponent as UpArrow } from "../../assets/Icons/UpArrow.svg";
import "./ReusableTable.scss";

const ReusableTable = ({
  columns,
  dataSource = [],
  rowKey = "key",
  loading = false,
  bordered = false,
  expandedRowRender = null,
  defaultExpandedRowKeys = [],
  pageSize = 10,
  setPageSize,
  CurrentPageNo = 1,
  triggerChange,
  totalItems = 0,
  rowSelection = null,
  pointer = false,
  className = "",
  rowClass = '',

  onRowClick = () => { },
}) => {
  const { Option } = Select;

  const [pagination, setPagination] = useState({
    current: CurrentPageNo,
    pageSize: pageSize || 10,
    total: totalItems,
  });
  const [tableData, setTableData] = useState([]);
  const totalPages = Math.ceil(pagination.total / pagination.pageSize);
  const tableRef = useRef(null);
  const [expandedRowKeys, setExpandedRowKeys] = useState(
    defaultExpandedRowKeys
  );


  useEffect(() => {
    setPagination({
      current: CurrentPageNo,
      pageSize: pageSize || 10,
      total: totalItems,
    });
  }, [CurrentPageNo, pageSize, totalItems]);

  useEffect(() => {
    setExpandedRowKeys(defaultExpandedRowKeys);
  }, []);

  const handleExpand = (expanded, record) => {
    if (expanded) {
      setExpandedRowKeys([record[rowKey]]);
    } else {
      setExpandedRowKeys([]);
    }
  };
  const expandable = expandedRowRender
    ? {
      expandedRowRender,
      expandedRowKeys,
      onExpand: handleExpand,
      expandIcon: ({ expanded, onExpand, record }) => (
        <span
          onClick={(e) => onExpand(record, e)}
          className={`custom-expand-icon ${expanded ? "expanded" : ""}`}
        >
          {expanded ? (
            <UpArrow style={{ cursor: "pointer" }} />
          ) : (
            <DownArrow style={{ cursor: "pointer" }} />
          )}
        </span>
      ),
      rowExpandable: () => true,
    }
    : undefined;

  useEffect(() => {
    const container = tableRef.current;
    const tableBody = container?.querySelector(".ant-table-content");

    if (!tableBody || !container) return;

    const handleScroll = () => {
      const scrollLeft = tableBody.scrollLeft;
      const scrollWidth = tableBody.scrollWidth;
      const clientWidth = tableBody.clientWidth;
      const maxScrollLeft = scrollWidth - clientWidth;

      container.classList.remove(
        "scroll-left",
        "scroll-middle",
        "scroll-right"
      );

      if (scrollWidth <= clientWidth + 1) return; // No scroll needed

      if (scrollLeft <= 0) {
        container.classList.add("scroll-right"); // Only right blur
      } else if (scrollLeft >= maxScrollLeft - 1) {
        container.classList.add("scroll-left"); // Only left blur
      } else {
        container.classList.add("scroll-middle"); // Both blurs
      }
    };

    handleScroll(); // initialize

    tableBody.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleScroll);

    return () => {
      tableBody.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [dataSource, pagination.current]);

  return (
    <div className="reusable-table-container" ref={tableRef}>
      <Space
        className="reusable-table"
        direction="vertical"
        style={{ width: "100%" }}
      >
        <div className="table-main-component">
          <Table
            columns={columns}
            dataSource={dataSource}
            rowKey={rowKey}
            loading={loading}
            pagination={false}
            bordered={bordered}
            className={`custom-table ${pointer ? "pointer" : ""} ${className}`}
            scroll={{ x: "max-content" }}
            expandable={expandable}
            rowSelection={rowSelection || undefined}
            rowClassName={rowClass}

            onRow={(record) => ({
              onClick: () => {
                onRowClick?.(record);
              },
            })}
          />
          <div className="table-blur-left" />
          <div className="table-blur-right" />
        </div>

          <div className="customPagination">
            <div className="customPaginationWrapper">
              <div className="pagination_total">
                Showing{" "}
                {Math.min(
                  (pagination.current - 1) * pagination.pageSize + 1,
                  pagination.total
                )}{" "}
                -{" "}
                {Math.min(
                  pagination.current * pagination.pageSize,
                  pagination.total
                )}{" "}
                of {pagination.total} items
              </div>

              <div className="paginationPages">
                <Button
                  onClick={() => {
                    setPagination({ ...pagination, current: 1 });
                    triggerChange?.(1, pagination.pageSize);
                  }}
                  disabled={pagination.current === 1}
                >
                  <NavToFirst />
                </Button>

                <ConfigProvider>
                  <Pagination
                    current={pagination.current}
                    showPrevNextJumpers
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    showSizeChanger={false}
                    onChange={(page) => {
                      setPagination({ ...pagination, current: page });
                      triggerChange?.(page, pagination.pageSize);
                    }}
                    itemRender={(page, type, originalElement) => {
                      if (type === "prev") {
                        return React.cloneElement(originalElement, {}, <PrevIcon />);
                      }
                      if (type === "next") {
                        return React.cloneElement(originalElement, {}, <NextIcon />);
                      }
                      return originalElement;
                    }}
                  />
                </ConfigProvider>


                <Button
                  onClick={() => {
                    setPagination({ ...pagination, current: totalPages });
                    triggerChange?.(totalPages, pagination.pageSize);
                  }}
                  disabled={pagination.current === totalPages}
                >
                  <NavToLast />
                </Button>
              </div>

              <div className="paginationSize">
                <Select
                  value={pagination.pageSize}
                  onChange={(newSize) => {
                    setPagination({
                      current: 1,
                      pageSize: newSize,
                      total: pagination.total,
                    });
                    setPageSize?.(newSize);
                    triggerChange?.(1, newSize);
                  }}
                >
                  {[5,10, 20, 50, 100].map((size) => (
                    <Option key={size} value={size}>
                      {size}
                    </Option>
                  ))}
                </Select>
                Items per Page
              </div>
            </div>
          </div>
       
      </Space>
    </div>
  );
};

export default ReusableTable;
